<template>
  <div id="app">
    <div class="header">
      <a href="/" v-if="logoUrl" class="logo-panel"><img :src="logoUrl" :alt="this.serverInfo.serviceName" /></a>
      <language-selector class="lg-selector" v-if="isReady" :urls="lgUrls"></language-selector>
    </div>

    <template v-if="isReady || !cookieEnabled || serverError">
      <div class="reg-container">
        <div class="reg-panel content-panel">
          <div v-if="!cookieEnabled">{{ $t(LABEL_COOKIE_REQUIRED) }}</div>

          <div v-else-if="serverError" style="text-align: center">
            {{ $t(LABEL_SERVER_ERROR) }} <br />
            {{ $t(LABEL_SERVER_ERROR_TRY_LATER) }}
          </div>

          <template v-else-if="!isEnabled">
            <b>{{ $t(LABEL_PASSWORD_RESET_DISABLED, [this.serverInfo.serviceName]) }}</b>
            <div style="margin-top: 1rem">
              <a :href="supportUrl" v-if="supportUrl">{{ $t(LABEL_CONTACT_SUPPORT) }}</a>
              <span v-else>{{ $t(LABEL_CONTACT_SUPPORT) }}</span>
            </div>
          </template>

          <template v-else>
            <div v-if="displayCaptcha">
              <CaptchaPanel
                :captchaKey="this.serverInfo.captchaApiKey"
                :locale="$i18n.locale"
                @captchaVerified="onCaptchaVerified"
                :captchaType="this.serverInfo.captchaType"
              ></CaptchaPanel>
            </div>

            <div v-show="showPasswordLinkSubmit">
              <PasswordResetLink :serviceName="this.serverInfo.serviceName" :displayBack="hasBack" @back="goBack"></PasswordResetLink>
            </div>

            <PasswordReset
              :passwordMaxLength="this.serverInfo.passwordMaxLength"
              :passwordMinLength="this.serverInfo.passwordMinLength"
              :resetInfo="this.passwordResetResult"
              :passwordKeyExpiration="this.serverInfo.keyExpiration"
              :signInUrl="signInUrl"
              v-on:retryLink="onResetLinkRetry"
              v-if="displayPasswordForm"
            ></PasswordReset>
          </template>
        </div>
      </div>
    </template>
    <div v-else class="loader-ctn"><div class="loader"></div></div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, defineComponent, ref, toRef } from 'vue';
import { getStartupLocale, setLocale, useI18n, setSupportedLocales } from '@/i18n';

import LanguageSelector from '@/components/lang-select.vue';
import isCookieEnabled from '@/libs/cookieCheck';

import CaptchaPanel from '@/components/captcha-panel.vue';
import {
  LABEL_CONTACT_SUPPORT,
  LABEL_COOKIE_REQUIRED,
  LABEL_ERROR_OCCURED,
  LABEL_PASSWORD_RESET_DISABLED,
  LABEL_SERVER_ERROR,
  LABEL_SERVER_ERROR_TRY_LATER,
} from '@/libs/i18nLabels';
import regInfo from '@/libs/querystringInfo';
import PasswordResetLink from './PasswordResetLink.vue';
import PasswordReset from './PasswordReset.vue';
import passwordState, { CaptachCallback, PasswordServerInfo, PasswordStateHandler } from './passwordState';

export default defineComponent({
  components: { LanguageSelector, PasswordResetLink, PasswordReset, CaptchaPanel },
  name: 'App',

  setup() {
    const isReady = ref(false);
    const isEnabled = ref(true);

    const displayCaptcha = ref(false);
    const captchaCallback = ref<CaptachCallback | null>(null);

    const displayPasswordForm = ref(false);
    const cookieEnabled = ref(isCookieEnabled());
    const serverError = ref(false);
    const serverInfo = ref<PasswordServerInfo | null>(null);

    const passwordResetResult = toRef(passwordState.state, 'passwordResetResult');

    function onResetLinkRetry() {
      displayPasswordForm.value = false;
    }

    function onCaptchaVerified(key: string) {
      captchaCallback.value?.onCaptchaResolved(key);
      // hide captcha
      displayCaptcha.value = false;
      captchaCallback.value = null;
    }

    const logoUrl = computed(() => {
      if (passwordState.isMailfence()) {
        // eslint-disable-next-line global-require
        return require('@/assets/mailfence.svg');
      }

      const info = serverInfo.value;

      if (!info || !info.ui) {
        return null;
      }

      return info.ui.logoUrl;
    });

    const supportUrl = computed(() => {
      const info = serverInfo.value;

      if (!info?.ui?.supportPageUrl) {
        return '/';
      }
      return info.ui.supportPageUrl;
    });

    const signInUrl = computed(() => {
      const info = serverInfo.value;

      if (!info?.ui?.signinUrl) {
        return `/sw?type=L&state=0&lf=${passwordState.state.lf}`;
      }
      return info.ui.signinUrl;
    });

    const showPasswordLinkSubmit = computed(() => {
      if (displayCaptcha.value) {
        return false;
      }

      if (displayPasswordForm.value) {
        return false;
      }
      return true;
    });

    function goBack() {
      window.history.back();
    }

    return {
      isReady,
      displayPasswordForm,
      cookieEnabled,
      serverError,
      serverInfo,
      isEnabled,
      onResetLinkRetry,
      logoUrl,
      supportUrl,
      signInUrl,
      displayCaptcha,
      captchaCallback,
      onCaptchaVerified,
      passwordResetResult,
      showPasswordLinkSubmit,

      hasBack: !!regInfo.backBtn,
      goBack,

      // eslint-disable-next-line no-underscore-dangle
      lgUrls: (window as any)._lgUrls,

      LABEL_COOKIE_REQUIRED,
      LABEL_PASSWORD_RESET_DISABLED,
      LABEL_CONTACT_SUPPORT,
      LABEL_SERVER_ERROR,
      LABEL_SERVER_ERROR_TRY_LATER,
    };
  },

  async mounted() {
    this.isReady = false;
    const i18n = useI18n();

    const stateHandler: PasswordStateHandler = {
      displayError: (message) => {
        const displayMessage = message.length ? message : `${this.$t(LABEL_ERROR_OCCURED)}`;
        // https://www.npmjs.com/package/vue-toast-notification
        this.$toast.open({ message: displayMessage, type: 'error', duration: 5000, position: 'top' });
      },

      displayCaptcha: (callback) => {
        this.displayCaptcha = true;
        this.captchaCallback = callback;
      },
    };

    passwordState.init(stateHandler);

    try {
      this.serverInfo = await passwordState.fetchServerInfo();
    } catch (err) {
      this.serverError = true;
      return;
    }
    // set locale after setting supported locales
    if (this.serverInfo.ui.supportedLanguages) {
      const supportedLanguages = this.serverInfo.ui.supportedLanguages as string;
      setSupportedLocales(supportedLanguages.split(','));
    }
    const locale = getStartupLocale();
    await setLocale(i18n, locale);

    if (this.serverInfo.disabled) {
      this.isEnabled = false;
    } else {
      this.isEnabled = true;
      this.displayPasswordForm = (passwordState.state.passwordResetResult && true) || false;
    }
    this.isReady = true;
  },
});
</script>
